// Kaiya Solutions — Enterprise Compliance & Risk Infrastructure Platform
// Data feeds Header, CaseStudies, Contact, and useContent hook.
// All copy aligns with the confirmed platform positioning:
// GrantGuard, VendorGuard, PROOF, CouncilGuard, ManifestGuard, ECom Protect.

export const navigation = [
  { name: 'Platform', href: '/kaiya-platform' },
  { name: 'Evidence Ledger', href: '/evidence-ledger' },
  { name: 'Protection Layers', href: '/protection-layers', isDropdown: true },
  { name: 'Risk Intelligence', href: '/risk-intelligence' },
  { name: 'About', href: '/about' },
];

export const protectionLayersDropdown = [
  { name: 'PROOF', href: '/proof', description: 'Job completion verification & dispute defense' },
  { name: 'GrantGuard', href: '/grantguard', description: 'Grant compliance & audit protection' },
  { name: 'GrantGuard for Tribal Governments', href: '/grantguard-tribal', description: '2 CFR 200 & Single Audit for sovereign nations' },
  { name: 'CouncilGuard', href: '/councilguard', description: 'Governance records & decision infrastructure' },
  { name: 'ManifestGuard', href: '/manifestguard', description: 'Shipping & customs compliance' },
  { name: 'VendorGuard', href: '/vendorguard', description: 'Vendor due diligence documentation' },
  { name: 'ECom Protect', href: '/ecom-protect', description: 'Chargeback defense & recovery' },
];

export const solutionsDropdown = [
  { name: 'PROOF', href: '/proof', description: 'Job completion verification & dispute defense' },
  { name: 'GrantGuard', href: '/grantguard', description: 'Grant compliance & audit protection' },
  { name: 'ManifestGuard', href: '/manifestguard', description: 'Shipping & customs compliance' },
  { name: 'VendorGuard', href: '/vendorguard', description: 'Vendor due diligence documentation' },
  { name: 'ECom Protect', href: '/ecom-protect', description: 'Chargeback defense & recovery' },
];

export const heroContent = {
  headline: 'Risk Infrastructure for Modern Operations',
  subheadline: 'Kaiya installs the control and accountability layer that modern organizations lack. We provide operational oversight, audit defensibility, and documented verification across every regulated interaction and transaction.',
  ctaText: 'Request Risk Assessment',
  ctaLink: '/risk-assessment',
  secondaryCta: 'Learn How It Works',
  secondaryCtaLink: '/risk-infrastructure',
};

export const valuePillars = [
  {
    id: 1,
    title: 'Audit Defensibility',
    description: 'Every action documented, timestamped, and immutable. When auditors, regulators, or investigators arrive, evidence packages are already assembled.',
    icon: 'ShieldCheck',
  },
  {
    id: 2,
    title: 'Traceable Evidence',
    description: 'A unified evidence ledger tracks compliance events across grants, vendors, contractors, shipments, and governance decisions — one source of truth.',
    icon: 'Database',
  },
  {
    id: 3,
    title: 'AI-Powered Gap Detection',
    description: 'GPT-4o identifies compliance gaps, missing documentation, and control failures before regulators do. Recommendations are structured and actionable.',
    icon: 'Sparkles',
  },
];

export const howItWorks = [
  {
    step: 1,
    title: 'Risk Assessment',
    description: 'We map your operational risk surface — grant compliance obligations, vendor exposure, contractor documentation gaps, audit readiness posture — and identify where evidence infrastructure is missing.',
  },
  {
    step: 2,
    title: 'Module Deployment',
    description: 'The right protection modules are configured for your operations — GrantGuard, VendorGuard, PROOF, CouncilGuard, ManifestGuard — sharing one unified authentication, one audit trail, one evidence ledger.',
  },
  {
    step: 3,
    title: 'Continuous Monitoring',
    description: 'Automated scheduling handles deadline tracking, expiration monitoring, and compliance recomputation. AI flags emerging risks. Every write lands in the immutable evidence ledger for future audits.',
  },
];

export const industries = [
  {
    id: 1,
    name: 'Federal Grant Recipients',
    description: 'Municipal governments, tribal councils, nonprofits, and universities managing federal awards. 2 CFR 200 compliance, Single Audit preparation, subrecipient monitoring, SEFA reporting.',
    icon: 'Landmark',
  },
  {
    id: 2,
    name: 'Enterprises & Regulated Operators',
    description: 'Companies with third-party vendor exposure, contractor verification requirements, or cross-border shipping compliance obligations. VendorGuard, PROOF, and ManifestGuard address each domain.',
    icon: 'Building2',
  },
];

export const solutions = [
  {
    id: 1,
    title: 'Federal Grant Compliance (GrantGuard)',
    description: 'Purpose-built for 2 CFR Part 200 recipients. Grant lifecycle management, control mapping, gap detection, SEFA builder, subrecipient monitoring, mock audits, and audit-ready evidence packaging.',
    outcomes: [
      'Complete 2 CFR 200 control mapping',
      'AI-powered compliance gap detection',
      'SEFA (Schedule of Expenditures of Federal Awards) automation',
      'Subrecipient monitoring dashboard',
      'Mock audit simulation engine',
      'Audit-ready evidence package exports',
    ],
    positioning: 'The compliance infrastructure federal grant recipients need to survive Single Audit.',
    icon: 'FileCheck',
  },
];

export const industryDetails = [
  {
    id: 1,
    name: 'Federal Grant Recipients',
    slug: 'federal-grant-recipients',
    headline: 'Audit-Ready from Day One',
    description: 'Federal grant recipients live under 2 CFR Part 200 obligations, Single Audit requirements, and continuous compliance scrutiny. GrantGuard installs the structured evidence chain that spreadsheets cannot provide.',
    painPoints: [
      'Compliance documentation scattered across spreadsheets and shared drives',
      'Subrecipient monitoring done manually and inconsistently',
      'Audit preparation consumes weeks of staff time each year',
      'Gaps only surface after regulators find them',
    ],
    outcomes: [
      'Continuous 2 CFR 200 control coverage',
      'Automated subrecipient risk scoring',
      'Audit evidence packages generated on demand',
      'AI gap detection surfaces issues before regulators do',
    ],
    icon: 'Landmark',
  },
  {
    id: 2,
    name: 'Enterprises & Regulated Operators',
    slug: 'enterprises',
    headline: 'Operational Accountability at Scale',
    description: 'Enterprises with third-party vendor exposure, contractor verification requirements, or cross-border shipping obligations face growing compliance pressure. Kaiya installs the documentation layer that makes operations defensible.',
    painPoints: [
      'Vendor COI expiries missed until audit',
      'Contractor job completion documentation is fragmented',
      'Import/export documentation lacks a defensible trail',
      'No unified evidence chain across compliance domains',
    ],
    outcomes: [
      'VendorGuard: automated COI expiry tracking + risk scoring',
      'PROOF: dual-signature contractor completion records',
      'ManifestGuard: AI-assisted customs documentation',
      'Unified evidence ledger across every module',
    ],
    icon: 'Building2',
  },
];

// Case studies intentionally left empty until real, verified case studies are drafted.
// Do NOT reintroduce legacy case studies from prior positioning.
export const caseStudies = [];

export const faqItems = [
  {
    question: 'What is Kaiya Solutions?',
    answer: 'Kaiya Solutions is an AI-powered compliance and risk infrastructure platform. We provide purpose-built modules — GrantGuard (federal grant compliance), VendorGuard (vendor due diligence), PROOF (contractor verification), CouncilGuard (governance records), and ManifestGuard (customs compliance) — that share one authentication system, one immutable audit trail, and one unified evidence ledger.',
  },
  {
    question: 'Who uses Kaiya Solutions?',
    answer: 'Governments, tribal councils, nonprofits, federal grant recipients, enterprises with third-party vendor exposure, contractors, and regulated operators who need documented, defensible operational evidence for audits, inspections, and regulatory reviews.',
  },
  {
    question: 'How does the AI work?',
    answer: 'Kaiya uses OpenAI GPT-4o and Whisper via structured prompts that produce validated JSON output — never free-form advice. AI identifies compliance gaps, extracts structured records from documents and transcripts, classifies risk, and generates audit-ready summaries. Every AI output is disclosed as AI-generated and reviewable by a human before it becomes a system-of-record entry.',
  },
  {
    question: 'What is the Evidence Ledger?',
    answer: 'The Evidence Ledger is a platform-wide, tamper-resistant record of every operational event across every module. Vendor COI uploads, grant expense approvals, contractor completions, meeting decisions, and shipment releases all land in one immutable, timestamped, searchable ledger.',
  },
  {
    question: 'Is my data secure?',
    answer: 'Yes. Data is encrypted in transit (TLS 1.2+) and at rest. Access is enforced by role-based permissions at every API endpoint. Multi-tenant data isolation is applied at the organization level. Sensitive documents remain within your tenant. See our Security Policy and Data Processing Addendum for full details.',
  },
  {
    question: 'How long does implementation take?',
    answer: 'Most modules are operational within 2–4 weeks. GrantGuard implementations for federal recipients typically complete in 3–6 weeks depending on grant portfolio size and existing documentation quality.',
  },
  {
    question: 'Does Kaiya provide legal or audit advice?',
    answer: 'No. Kaiya provides infrastructure for documenting, tracking, and evidencing compliance activity. We do not provide legal, accounting, or licensed audit certification advice. AI-generated content requires human review per the AI Disclosure and Terms of Service.',
  },
  {
    question: 'Is there a trial?',
    answer: 'Yes. New accounts receive a 14-day trial to ManifestGuard and GrantGuard by default. Additional modules can be enabled by contacting your account administrator.',
  },
  {
    question: 'Do you offer white-label deployments?',
    answer: 'Yes. GrantGuard Enterprise supports full white-label branding with per-tenant configuration. The framework is extendable to other modules on request.',
  },
  {
    question: 'How is Kaiya different from generic compliance software?',
    answer: 'Generic compliance tools are IT/SOC2 focused. Kaiya is purpose-built for the compliance domains regulated operators actually face — federal grants (2 CFR 200), vendor documentation (COIs, W-9s, insurance), contractor verification, governance records, and customs compliance. Every module ships with domain-specific workflows, not generic checklists.',
  },
];

export const aboutContent = {
  mission: 'Kaiya Solutions exists to provide the operational infrastructure that regulated organizations lack. We believe every business interaction, transaction, and compliance obligation should be documented, verified, and defensible.',
  story: 'We built Kaiya after seeing how many organizations operate without proper accountability systems. Grants are clawed back because there is no traceability. Audits fail because there is no documentation. Compliance lapses because there is no verification. Chargebacks succeed because there is no dispute evidence. Kaiya installs the control layer that prevents these failures across every operational domain.',
  values: [
    {
      title: 'Accountability First',
      description: 'Every action documented. Every transaction verified. Every interaction recorded. Your operations become defensible.',
    },
    {
      title: 'Risk-Centered Design',
      description: 'Built specifically to address operational risk. We understand audit requirements, compliance standards, and the cost of documentation failures.',
    },
    {
      title: 'Verification at Every Step',
      description: 'Nothing falls through the cracks. Our systems ensure every critical touchpoint has proper documentation and dual-party verification where appropriate.',
    },
  ],
};

export const footerContent = {
  company: 'Kaiya Solutions',
  tagline: 'Risk infrastructure that provides operational oversight and audit defensibility.',
  poweredBy: '',
  links: [
    { name: 'Risk Infrastructure', href: '/risk-infrastructure' },
    { name: 'Protection Layers', href: '/protection-layers' },
    { name: 'Industries', href: '/industries' },
    { name: 'About', href: '/about' },
    { name: 'Request Risk Assessment', href: '/risk-assessment' },
    { name: 'Case Studies', href: '/case-studies' },
    { name: 'FAQ', href: '/faq' },
    { name: 'Privacy Policy', href: '/privacy' },
  ],
};

export const contactForm = {
  headline: 'Request a Risk Assessment',
  subheadline: 'Our team will analyze your current operational gaps and identify where you lack traceability, verification, and documentation. No obligation.',
  fields: [
    { name: 'name', label: 'Your Name', type: 'text', required: true },
    { name: 'email', label: 'Work Email', type: 'email', required: true },
    { name: 'company', label: 'Organization Name', type: 'text', required: true },
    { name: 'role', label: 'Your Role', type: 'select', options: ['Operations', 'Compliance', 'Finance', 'Executive', 'Other'], required: true },
  ],
  submitText: 'Request Risk Assessment',
};
